import { useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, Link, useSearchParams } from 'react-router';

import { AppRoutes, THROTTLE_LIMIT } from '@/constants';
import { useDebounce, useThrottle } from '@/hooks';
import { navigate } from '@/providers';
import { SortDirection } from '@/types';
import { formatDate } from '@/utils';

import { CloseIcon, PlusIcon } from '@/assets/icons';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Loading,
  MultiSelect,
  NotFoundResult,
  Pagination,
  Search,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/common';

import { ROLE_STATUS_OPTIONS } from '@/features/role-assignments/constants';
import { RoleStatus } from '@/features/role-assignments/enums';
import { useRoleAssignmentQuery } from '@/features/role-assignments/hooks';
import { IRoleAssignmentSearchingPayload } from '@/features/role-assignments/interfaces';
import { getRoleStatusLabel, getRoleStatusVariant } from '@/features/role-assignments/utils';

const RoleAssignmentList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = Number(searchParams.get('page'));
  const initialPage = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;

  const [searchQuery, setSearchQuery] = useState<string>(searchParams.get('search') || '');
  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [pageSize] = useState<number>(10);
  const [roleIds, setRoleIds] = useState<string[]>(
    searchParams.get('roles')?.split(',').filter(Boolean) || [],
  );
  const [roleStatus, setRoleStatus] = useState<string[]>(
    searchParams.get('status')?.split(',').filter(Boolean) || [],
  );
  const [sortBy, setSortBy] = useState<string>(searchParams.get('sortBy') || 'lastModified');
  const [isDescending, setIsDescending] = useState<boolean>(searchParams.get('desc') !== 'false');
  const previousFilterKeyRef = useRef<string | null>(null);

  // Handle column sorting
  const handleSort = (columnKey: string, direction: SortDirection) => {
    if (direction === null) {
      setSortBy('');
      setIsDescending(true);
    } else {
      setSortBy(columnKey);
      setIsDescending(direction === 'desc');
    }
  };

  // Debounce search query
  const debouncedSearchQuery = useDebounce(searchQuery);

  // Throttle filters and sorting
  const throttledRoles = useThrottle(roleIds, THROTTLE_LIMIT, true);
  const throttledRoleStatus = useThrottle(roleStatus, THROTTLE_LIMIT, true);
  const throttledSortBy = useThrottle(sortBy, THROTTLE_LIMIT, true);
  const throttledIsDescending = useThrottle(isDescending, THROTTLE_LIMIT, true);

  // Build API payload
  const apiPayload = useMemo(() => {
    const payload: IRoleAssignmentSearchingPayload = {
      page: currentPage,
      isDescending: throttledIsDescending ?? true,
    };

    if (debouncedSearchQuery) {
      payload.searchBy = debouncedSearchQuery;
    }

    if (throttledRoles && throttledRoles.length === 1) {
      payload.roleId = throttledRoles[0];
    }

    if (throttledRoleStatus && throttledRoleStatus.length === 1) {
      payload.status = Number(throttledRoleStatus[0]) as RoleStatus;
    }

    if (throttledSortBy) {
      payload.sortBy = throttledSortBy;
    }

    return payload;
  }, [
    debouncedSearchQuery,
    currentPage,
    throttledRoles,
    throttledRoleStatus,
    throttledSortBy,
    throttledIsDescending,
  ]);

  // Fetch data from API
  const { rolesQuery, useRolesSearching } = useRoleAssignmentQuery();
  const roles = rolesQuery.data || [];
  const { data, isLoading, isFetching, isError } = useRolesSearching(apiPayload);

  const roleAssignments = useMemo(() => data?.data || [], [data?.data]);
  const totalPages = useMemo(() => data?.metadata.totalPages || 0, [data?.metadata.totalPages]);
  const totalItems = useMemo(() => data?.metadata.totalItems || 0, [data?.metadata.totalItems]);

  const isFiltered = useMemo(
    () => searchQuery.length > 0 || roleIds.length > 0 || roleStatus.length > 0,
    [searchQuery, roleIds, roleStatus],
  );

  const isEmpty = useMemo(() => roleAssignments.length === 0, [roleAssignments]);

  const onClearFilters = () => {
    setSearchQuery('');
    setRoleIds([]);
    setRoleStatus([]);
  };

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();

    if (currentPage > 1) params.set('page', String(currentPage));
    if (searchQuery) params.set('search', searchQuery);
    if (roleIds.length) params.set('roles', roleIds.join(','));
    if (roleStatus.length) params.set('status', roleStatus.join(','));
    if (sortBy !== 'lastModified') params.set('sortBy', sortBy);
    if (!isDescending) params.set('desc', 'false');

    setSearchParams(params, { replace: true });
  }, [searchQuery, roleIds, roleStatus, sortBy, isDescending, setSearchParams, currentPage]);

  // Reset to page 1 when filters actually change (skip initial mount + throttle settling)
  useEffect(() => {
    const filterKey = JSON.stringify([
      debouncedSearchQuery,
      throttledRoles,
      throttledRoleStatus,
      throttledSortBy,
      throttledIsDescending,
    ]);

    if (previousFilterKeyRef.current === null) {
      previousFilterKeyRef.current = filterKey;
      return;
    }

    if (previousFilterKeyRef.current !== filterKey) {
      previousFilterKeyRef.current = filterKey;
      setCurrentPage(1);
    }
  }, [
    debouncedSearchQuery,
    throttledRoles,
    throttledRoleStatus,
    throttledSortBy,
    throttledIsDescending,
  ]);

  if (isError) {
    navigate(AppRoutes.INTERNAL_SERVER_ERROR);
  }

  return (
    <div className="w-full flex flex-col items-start justify-start">
      <h1 className="font-bold text-[32px] text-neutral-n10">Role Assignment</h1>
      <div className="flex flex-row gap-2 flex-wrap items-start justify-between w-full mt-8 mb-5">
        <div className="flex flex-row flex-wrap items-start justify-start gap-2">
          <Search
            placeholder="Search by Full Name, Email or Employee ID"
            className="w-[330px] sm:w-[362px] gap-2 !h-11"
            style={{ height: '44px', paddingRight: '12px' }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <MultiSelect
            options={roles}
            defaultValue={roleIds}
            placeholder="Role"
            maxCount={1}
            className="!w-fit !min-w-[290px]"
            onValueChange={(values) => {
              setRoleIds(values);
            }}
          />
          <MultiSelect
            options={ROLE_STATUS_OPTIONS}
            defaultValue={roleStatus}
            placeholder="Status"
            maxCount={1}
            className="!w-fit !min-w-[270px]"
            onValueChange={(values) => {
              setRoleStatus(values);
            }}
          />
          {isFiltered && (
            <Button
              variant="link"
              prefixIcon={<CloseIcon className="w-5 h-5" />}
              onClick={onClearFilters}
              className="!pl-1 !h-11"
            >
              Clear
            </Button>
          )}
        </div>
        <Button
          variant="primary"
          prefixIcon={<PlusIcon />}
          className="font-semibold text-sm px-4 rounded-lg whitespace-nowrap text-ellipsis !h-11 !w-[138px]"
          onClick={() => navigate(AppRoutes.ROLE_ASSIGNMENT_CREATION)}
        >
          Assign Role
        </Button>
      </div>
      <div className="w-full relative">
        {isEmpty && !isLoading && !isFetching ? (
          <NotFoundResult />
        ) : (
          <>
            <Table className="mt-4" size="lg">
              <TableHeader>
                <TableRow>
                  <TableHead
                    sortable
                    sortDirection={sortBy === 'id' ? (isDescending ? 'desc' : 'asc') : null}
                    onSort={(direction) => handleSort('id', direction)}
                  >
                    ID
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={sortBy === 'profileId' ? (isDescending ? 'desc' : 'asc') : null}
                    onSort={(direction) => handleSort('profileId', direction)}
                  >
                    Face Profile ID
                  </TableHead>
                  <TableHead
                    sortable
                    className="w-[13%]"
                    sortDirection={sortBy === 'fullName' ? (isDescending ? 'desc' : 'asc') : null}
                    onSort={(direction) => handleSort('fullName', direction)}
                  >
                    Full Name
                  </TableHead>
                  <TableHead
                    sortable
                    className="w-[13%]"
                    sortDirection={sortBy === 'employeeId' ? (isDescending ? 'desc' : 'asc') : null}
                    onSort={(direction) => handleSort('employeeId', direction)}
                  >
                    Employee ID
                  </TableHead>
                  <TableHead
                    sortable
                    className="w-[13%]"
                    sortDirection={sortBy === 'email' ? (isDescending ? 'desc' : 'asc') : null}
                    onSort={(direction) => handleSort('email', direction)}
                  >
                    Email
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={sortBy === 'roleName' ? (isDescending ? 'desc' : 'asc') : null}
                    onSort={(direction) => handleSort('roleName', direction)}
                  >
                    Role
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={sortBy === 'status' ? (isDescending ? 'desc' : 'asc') : null}
                    onSort={(direction) => handleSort('status', direction)}
                  >
                    Status
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={
                      sortBy === 'lastModified' ? (isDescending ? 'desc' : 'asc') : null
                    }
                    onSort={(direction) => handleSort('lastModified', direction)}
                  >
                    Last Modified
                  </TableHead>
                  <TableHead
                    sortable
                    sortDirection={
                      sortBy === 'lastModifiedBy' ? (isDescending ? 'desc' : 'asc') : null
                    }
                    onSort={(direction) => handleSort('lastModifiedBy', direction)}
                  >
                    Last Modified By
                  </TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roleAssignments.map((roleAssignment) => (
                  <TableRow key={roleAssignment.id} rowId={roleAssignment.id}>
                    <TableCell className="font-medium">{roleAssignment.id}</TableCell>
                    <TableCell className="font-medium">{roleAssignment.profileId}</TableCell>
                    <TableCell className="font-medium underline text-brand-primary-base max-w-[274px]">
                      <Link
                        to={generatePath(AppRoutes.ROLE_ASSIGNMENT_DETAILS, {
                          id: roleAssignment.id,
                        })}
                        className="block truncate"
                        title={roleAssignment.fullName}
                      >
                        {roleAssignment.fullName}
                      </Link>
                    </TableCell>
                    <TableCell className="font-medium">{roleAssignment.employeeId}</TableCell>
                    <TableCell className="font-medium max-w-[264px]">
                      <span className="block truncate" title={roleAssignment.email}>
                        {roleAssignment.email}
                      </span>
                    </TableCell>
                    <TableCell className="font-medium">{roleAssignment.roleName}</TableCell>
                    <TableCell
                      cellType="tag"
                      tagVariant={getRoleStatusVariant(roleAssignment.status)}
                    >
                      {getRoleStatusLabel(roleAssignment.status)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {formatDate({
                        date: new Date(roleAssignment.lastModified),
                        hasTime: true,
                      })}
                    </TableCell>
                    <TableCell className="font-medium max-w-[185px]">
                      <span className="block truncate" title={roleAssignment.lastModifiedBy}>
                        {roleAssignment.lastModifiedBy}
                      </span>
                    </TableCell>
                    <TableCell align="right" cellType="custom" className="!p-0">
                      <DropdownMenu>
                        <DropdownMenuTrigger
                          className="w-[63px] aspect-square"
                          asChild
                        ></DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="start"
                          side="left"
                          alignOffset={26}
                          sideOffset={-16}
                        >
                          <DropdownMenuItem
                            onClick={() =>
                              navigate(
                                generatePath(AppRoutes.ROLE_ASSIGNMENT_DETAILS, {
                                  id: roleAssignment.id,
                                }),
                              )
                            }
                          >
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() =>
                              navigate(
                                generatePath(AppRoutes.ROLE_ASSIGNMENT_UPDATE, {
                                  id: roleAssignment.id,
                                }),
                              )
                            }
                          >
                            Edit
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </>
        )}
        {(isLoading || isFetching) && <Loading fullScreen />}
      </div>
    </div>
  );
};
export default RoleAssignmentList;
